import { Icon, type IconName } from "../ui/Icon";
import { P } from "../../lib/permissions";

/**
 * The navigation model.
 *
 * Grouped exactly as the approved screens show it — COMMAND, COMMERCIAL,
 * DELIVERY, CONTROL, SYSTEM — with each entry naming the permission it needs.
 * The sidebar hides what the signed-in user cannot reach, and the router
 * refuses it as well: the menu is a courtesy, the guard is the control.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: IconName;
  /** Any one of these permissions makes the item visible. */
  permissions: string[];
  /** Module key; hidden when the tenant has the module switched off. */
  module?: string;
  /** True when the destination needs an event in context. */
  needsEvent?: boolean;
  /** Portal audiences the item is for. Absent means every audience; internal navigation never sets it. */
  audiences?: string[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    label: "Command",
    items: [
      {
        label: "Executive Command",
        to: "/executive",
        icon: "target",
        permissions: [P.ExecutiveView],
        module: "command",
      },
      {
        label: "Global Overview",
        to: "/overview",
        icon: "grid",
        permissions: [P.CommandView],
        module: "command",
      },
      {
        label: "My Work",
        to: "/work",
        icon: "checkSquare",
        permissions: [P.WorkView],
        module: "work",
      },
      {
        label: "AI Insights",
        to: "/ai",
        icon: "sparkle",
        permissions: [P.AiView],
        module: "ai",
      },
      {
        label: "Alerts & Actions",
        to: "/alerts",
        icon: "bell",
        permissions: [P.WorkView],
        module: "work",
      },
      {
        label: "Live Operations",
        to: "/live",
        icon: "bolt",
        permissions: [P.OnsiteView],
        module: "onsite",
      },
      {
        label: "Incidents",
        to: "/incidents",
        icon: "alert",
        permissions: [P.WorkView],
        module: "work",
      },
    ],
  },
  {
    label: "Commercial",
    items: [
      {
        label: "Events & Portfolio",
        to: "/events",
        icon: "calendar",
        permissions: [P.EventsView],
        module: "events",
      },
      {
        label: "Sales Pipeline",
        to: "/pipeline",
        icon: "trend",
        permissions: [P.CrmView],
        module: "crm",
      },
      {
        label: "Clients",
        to: "/clients",
        icon: "building",
        permissions: [P.ClientsView],
        module: "clients",
      },
      {
        label: "Partners & Sponsors",
        to: "/sponsors",
        icon: "handshake",
        permissions: [P.SponsorsView],
        module: "sponsors",
        needsEvent: true,
      },
      {
        label: "Marketing",
        to: "/communications",
        icon: "megaphone",
        permissions: [P.CommunicationsView],
        module: "communications",
        needsEvent: true,
      },
    ],
  },
  {
    label: "Delivery",
    items: [
      {
        label: "Operations",
        to: "/projects",
        icon: "layers",
        permissions: [P.ProjectsView],
        module: "projects",
      },
      {
        label: "Venues",
        to: "/venues",
        icon: "building",
        permissions: [P.EventsView],
      },
      {
        label: "Scientific",
        to: "/scientific",
        icon: "book",
        permissions: [P.ScientificView],
        module: "scientific",
        needsEvent: true,
      },
      {
        label: "Abstracts",
        to: "/abstracts",
        icon: "doc",
        permissions: [P.AbstractsView],
        module: "abstracts",
        needsEvent: true,
      },
      {
        label: "Attendees",
        to: "/registration",
        icon: "users",
        permissions: [P.RegistrationView],
        module: "registration",
        needsEvent: true,
      },
      {
        label: "Travel & Hospitality",
        to: "/travel",
        icon: "plane",
        permissions: [P.TravelView],
        module: "travel",
        needsEvent: true,
      },
      {
        label: "Creative",
        to: "/creative",
        icon: "palette",
        permissions: [P.CreativeView],
        module: "creative",
        needsEvent: true,
      },
      {
        label: "Production",
        to: "/production",
        icon: "box",
        permissions: [P.ProductionView],
        module: "production",
        needsEvent: true,
      },
      {
        label: "Vendors",
        to: "/procurement",
        icon: "clipboard",
        permissions: [P.VendorsView],
        module: "vendors",
        needsEvent: true,
      },
      {
        label: "Website",
        to: "/website",
        icon: "globe",
        permissions: [P.WebsiteView],
        module: "website",
        needsEvent: true,
      },
      {
        label: "On-Site",
        to: "/onsite",
        icon: "scan",
        permissions: [P.OnsiteView],
        module: "onsite",
        needsEvent: true,
      },
      {
        // A check-in steward records arrivals without reading the on-site dashboard, so the desk is
        // named by the permission that actually opens it.
        label: "Check-in desk",
        to: "/onsite/checkin",
        icon: "scan",
        permissions: [P.OnsiteCreate],
        module: "onsite",
        needsEvent: true,
      },
      {
        label: "Staff",
        to: "/staff",
        icon: "users",
        permissions: [P.OnsiteView],
        module: "onsite",
        needsEvent: true,
      },
    ],
  },
  {
    label: "Control",
    items: [
      {
        label: "Finance",
        to: "/finance",
        icon: "money",
        permissions: [P.FinanceView],
        module: "finance",
        needsEvent: true,
      },
      {
        label: "People & Teams",
        to: "/people",
        icon: "user",
        permissions: [P.PeopleView],
        module: "people",
      },
      {
        label: "Documents",
        to: "/documents",
        icon: "file",
        permissions: [P.DocumentsView],
        module: "documents",
      },
      {
        label: "Approvals",
        to: "/approvals",
        icon: "check",
        permissions: [P.WorkView],
        module: "work",
      },
      {
        label: "Risk & Compliance",
        to: "/risks",
        icon: "shield",
        permissions: [P.WorkView],
        module: "work",
      },
      {
        label: "Reports",
        to: "/analytics",
        icon: "chart",
        permissions: [P.AnalyticsView],
        module: "analytics",
      },
    ],
  },
  {
    label: "System",
    items: [
      {
        label: "Automations",
        to: "/automations",
        icon: "bolt",
        permissions: [P.AutomationsView],
        module: "automations",
      },
      {
        label: "Integrations",
        to: "/integrations",
        icon: "plug",
        permissions: [P.IntegrationsView],
        module: "integrations",
      },
      {
        label: "Administration",
        to: "/administration",
        icon: "cog",
        permissions: [P.AdminView, P.AdminUsers],
        module: "administration",
      },
      {
        label: "Platform",
        to: "/saas",
        icon: "globe",
        permissions: [P.SaasView],
        module: "saas",
      },
    ],
  },
];

/**
 * The tabs of an event workspace (/events/:eventId): the event's own view of each module it uses.
 *
 * A tab shows when the user holds one of its permissions and the event has its module switched on. Paths are
 * relative to the workspace, and each opens the same module screen the sidebar does, on the workspace's event.
 */
export interface WorkspaceTab {
  label: string;
  /** Relative to /events/:eventId; empty for the overview. */
  path: string;
  permissions: string[];
  /** Event module key. The tab leaves while the event has the module switched off. */
  module?: string;
}

export const EVENT_WORKSPACE_TABS: WorkspaceTab[] = [
  { label: "Overview", path: "", permissions: [P.EventsView] },
  { label: "Scientific", path: "scientific", permissions: [P.ScientificView], module: "scientific" },
  { label: "Abstracts", path: "abstracts", permissions: [P.AbstractsView], module: "abstracts" },
  { label: "Registration", path: "registration", permissions: [P.RegistrationView], module: "registration" },
  { label: "Sponsors", path: "sponsors", permissions: [P.SponsorsView], module: "sponsors" },
  {
    label: "Exhibitors",
    path: "exhibitors",
    permissions: [P.ExhibitorsView, P.SponsorsView],
    module: "exhibitors",
  },
  { label: "Travel", path: "travel", permissions: [P.TravelView], module: "travel" },
  { label: "Creative", path: "creative", permissions: [P.CreativeView], module: "creative" },
  { label: "Production", path: "production", permissions: [P.ProductionView], module: "production" },
  { label: "Vendors", path: "vendors", permissions: [P.VendorsView], module: "vendors" },
  { label: "Finance", path: "finance", permissions: [P.FinanceView], module: "finance" },
  {
    label: "Marketing",
    path: "communications",
    permissions: [P.CommunicationsView],
    module: "communications",
  },
  { label: "Website", path: "website", permissions: [P.WebsiteView], module: "website" },
  { label: "On-Site", path: "onsite", permissions: [P.OnsiteView], module: "onsite" },
  { label: "Check-in", path: "onsite/checkin", permissions: [P.OnsiteCreate], module: "onsite" },
  { label: "Staff", path: "staff", permissions: [P.OnsiteView], module: "onsite" },
  { label: "Settings", path: "settings", permissions: [P.EventsEdit] },
];

/*
  Each portal user sees their own audience's sections, and only those their permissions open: approvals
  need the portal approval right and the account needs the financial view. The API refuses the rest
  whatever this menu shows.
*/
const CLIENT = ["Client"];

export const PORTAL_NAVIGATION: NavGroup[] = [
  {
    label: "Your events",
    items: [
      { label: "Overview", to: "/portal", icon: "home", permissions: [P.PortalView], audiences: CLIENT },
      { label: "Approvals", to: "/portal/approvals", icon: "check", permissions: [P.PortalApprove], audiences: CLIENT },
      { label: "Creative", to: "/portal/creative", icon: "palette", permissions: [P.PortalView], audiences: CLIENT },
      { label: "Shared work", to: "/portal/work", icon: "checkSquare", permissions: [P.PortalView], audiences: CLIENT },
      { label: "Documents", to: "/portal/documents", icon: "file", permissions: [P.PortalView], audiences: CLIENT },
      { label: "Finance", to: "/portal/finance", icon: "money", permissions: [P.FinanceView], audiences: CLIENT },
    ],
  },
  {
    label: "You",
    items: [
      { label: "Speaker hub", to: "/portal/speaker", icon: "mic", permissions: [P.PortalView], audiences: ["Speaker"] },
      {
        label: "Sponsorship",
        to: "/portal/sponsor",
        icon: "handshake",
        permissions: [P.PortalView],
        audiences: ["Sponsor", "Exhibitor"],
      },
      {
        label: "Abstract reviews",
        to: "/abstracts/review",
        icon: "book",
        permissions: [P.AbstractsReview],
        audiences: ["Reviewer"],
      },
      {
        label: "My registrations",
        to: "/portal/me",
        icon: "ticket",
        permissions: [P.PortalView],
        audiences: ["Participant", "Speaker"],
      },
      {
        label: "CME wallet",
        to: "/portal/cme",
        icon: "award",
        permissions: [P.PortalView],
        audiences: ["Participant", "Speaker"],
      },
    ],
  },
];

export const iconFor = (name: IconName) => Icon[name];
